/* SPEC-507 — which environment is this build running in?
 * Derived from the hostname only (never a server flag). Production and every other host
 * render with NO data-env attribute and the normal favicon.
 * SPEC-518 (supersedes the SPEC-510 lowercase host labels): the browser-tab title is the
 * brand title: SuryaIQ / Staging · SuryaIQ / Stats · SuryaIQ / Staging · Stats · SuryaIQ.
 * No tagline, no page name. The served HTML carries the same title without JS: a staging
 * build rewrites the head (brandHtml, run from vite.config.js); nginx rewrites it for stats.
 * VITE_ENV_OVERRIDE (production | staging | local) lets a local dev preview the staging look. */

export type Environment = 'production' | 'staging' | 'local';

const OVERRIDES: ReadonlyArray<string> = ['production', 'staging', 'local'];
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export const MAIN_SITE_URL = 'https://suryaiq.com';
export const STAGING_FAVICON = '/favicon-staging.svg';

export function resolveEnvironment(hostname: string | undefined, override?: string): Environment {
  const o = (override ?? '').trim().toLowerCase();
  if (OVERRIDES.includes(o)) return o as Environment;
  const host = (hostname ?? '').trim().toLowerCase();
  if (LOCAL_HOSTS.has(host)) return 'local';
  // "staging.suryaiq.com" and any sub-host beneath it ("stats.staging.suryaiq.com").
  if (host.split('.').includes('staging')) return 'staging';
  return 'production';
}

/** True on the Stats surface: the stats.* host, or the /stats route on any other host. */
export function isStatsSurface(hostname: string | undefined, pathname: string | undefined): boolean {
  const host = (hostname ?? '').trim().toLowerCase();
  if (host.split('.')[0] === 'stats') return true;
  const path = (pathname ?? '').trim().toLowerCase();
  return path === '/stats' || path.startsWith('/stats/');
}

export const BRAND_NAME = 'SuryaIQ';
/** U+00B7 MIDDLE DOT with one space either side. */
export const TITLE_SEP = ' · ';

/** Local calendar date as YYYY-MM-DD (the planner's day, not UTC). */
function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** SPEC-518: every user-facing download is named SuryaIQ-<what>-<YYYY-MM-DD>.<ext>.
 *  Idempotent: a SAFE name that already starts "SuryaIQ-" is kept (the leadership PDF
 *  carries its week); an unsafe one is rebuilt. A trailing ISO date on the stem is dropped;
 *  the brand date replaces it. Same rules as branded_filename in backend/export_utils.py
 *  (which stamps the London day; this stamps the browser's day). Used by lib/download.ts. */
export function brandedFilename(name: string, now: Date = new Date()): string {
  if (new RegExp(`^${BRAND_NAME}-[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)?$`).test(name)) return name;
  const dot = name.lastIndexOf('.');
  let stem = dot >= 0 ? name.slice(0, dot) : name;
  let ext = dot >= 0 ? name.slice(dot + 1) : '';
  stem = stem.replace(/[-_ ]?[0-9]{4}-[0-9]{2}-[0-9]{2}$/, '');
  stem = stem.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  stem = stem.replace(new RegExp(`^${BRAND_NAME}(-|$)`), '') || 'export';  // never "SuryaIQ-SuryaIQ-"
  ext = ext.replace(/[^A-Za-z0-9]+/g, '');
  return `${BRAND_NAME}-${stem}-${isoDay(now)}${ext ? `.${ext}` : ''}`;
}

/** SPEC-518: the whole browser-tab title. Fixed prefix order: Staging, Stats, SuryaIQ. */
export function brandTitle(env: Environment, statsSurface: boolean): string {
  const parts: string[] = [];
  if (env === 'staging') parts.push('Staging');
  if (statsSurface) parts.push('Stats');
  parts.push(BRAND_NAME);
  return parts.join(TITLE_SEP);
}

/** Title for useDocumentTitle. The page name only counts when withPage is set. */
export function documentTitleFor(env: Environment, statsSurface: boolean, page: string, withPage = false): string {
  const label = brandTitle(env, statsSurface);
  return withPage && page ? `${page}${TITLE_SEP}${label}` : label;
}

/** SPEC-510: what the environment markers render. Production and local show nothing. */
export function environmentMarkers(env: Environment): { chip: boolean; mainSiteHref: string | null } {
  return env === 'staging' ? { chip: true, mainSiteHref: MAIN_SITE_URL } : { chip: false, mainSiteHref: null };
}

// The few DOM members applyEnvironment touches, so tests pass plain fakes.
export interface EnvDoc {
  documentElement: { setAttribute(name: string, value: string): void };
  title: string;
  querySelector(selector: string): { setAttribute(name: string, value: string): void } | null;
}

/** Sets the tab title on every host; applies the staging look only on staging. Idempotent. */
export function applyEnvironment(doc: EnvDoc, env: Environment, statsSurface = false): Environment {
  doc.title = brandTitle(env, statsSurface);
  if (env !== 'staging') return env;
  doc.documentElement.setAttribute('data-env', 'staging');
  doc.querySelector('link[rel="icon"]')?.setAttribute('href', STAGING_FAVICON);
  return env;
}

export function currentEnvironment(): Environment {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  return resolveEnvironment(host, import.meta.env?.VITE_ENV_OVERRIDE as string | undefined);
}

export function currentStatsSurface(): boolean {
  if (typeof window === 'undefined') return false;
  return isStatsSurface(window.location.hostname, window.location.pathname);
}

/* ---- SPEC-518: the served HTML head (seen without JS) --------------------------------
 * index.html carries the production head. A staging build (VITE_ENV_OVERRIDE=staging, set
 * by deploy/remote_deploy.sh) rewrites it, so crawlers, link previews and bookmarks see the
 * staging title, icon and manifest. The stats surface is rewritten by nginx (sub_filter in
 * deploy/nginx.conf + nginx-staging.conf), which matches the exact <title> and og:title. */
export const STAGING_ORIGIN = 'https://staging.suryaiq.com';
/** --bg-base in theme.css (:root and :root[data-env="staging"]); environment.test.ts checks. */
export const THEME_COLOR = { production: '#111214', staging: '#16191E' } as const;

/** Each staging head swap: [exact production string, staging replacement]. */
export function stagingHeadSwaps(): Array<[string, string]> {
  const prod = brandTitle('production', false);
  const stg = brandTitle('staging', false);
  const themeColor = (scheme: string, c: string) =>
    `name="theme-color" media="(prefers-color-scheme: ${scheme})" content="${c}"`;
  return [
    ...['light', 'dark'].map((s): [string, string] =>
      [themeColor(s, THEME_COLOR.production), themeColor(s, THEME_COLOR.staging)]),
    [`<title>${prod}</title>`, `<title>${stg}</title>`],
    [`property="og:title" content="${prod}"`, `property="og:title" content="${stg}"`],
    // a home-screen or pinned staging app must not be named like production
    [`name="application-name" content="${prod}"`, `name="application-name" content="${stg}"`],
    [`name="apple-mobile-web-app-title" content="${prod}"`, `name="apple-mobile-web-app-title" content="${stg}"`],
    ['href="/favicon.svg"', `href="${STAGING_FAVICON}"`],
    ['href="/manifest.webmanifest"', 'href="/manifest-staging.webmanifest"'],
    ['href="/apple-touch-icon.png"', 'href="/apple-touch-icon-staging.png"'],
    [`content="${MAIN_SITE_URL}/og-image.png"`, `content="${STAGING_ORIGIN}/og-image.png"`],
  ];
}

/** Rewrites index.html for the build's environment. Production and local: unchanged.
 *  Throws when a production string is not found exactly once, so an edit to the head can
 *  never ship a staging build that still says "SuryaIQ". */
export function brandHtml(html: string, env: Environment): string {
  if (env !== 'staging') return html;
  let out = html;
  for (const [from, to] of stagingHeadSwaps()) {
    const n = out.split(from).length - 1;
    if (n !== 1) throw new Error(`SPEC-518 brandHtml: expected exactly one ${JSON.stringify(from)} in index.html, found ${n}`);
    out = out.replace(from, to);
  }
  return out;
}
